import { createRouter, createWebHashHistory } from 'vue-router'

import MainLayout from '@/layouts/MainLayout.vue'
import { config } from '@/lib/helpers/config.helpers'
import LogoutView from '@/views/LogoutView.vue'
import Store from '../store'
import ClientInvoicePayments from '../views/ClientInvoicePayments/List'
import CreateClient from '../views/clients/Create'
import EditClient from '../views/clients/Edit'
import ListClients from '../views/clients/List'
import ClientUsers from '../views/clients/Users'
import Dashboard from '../views/Dashboard'
import UserLinks from '../views/links/List'
import CreatePayments from '../views/payments/Create'
import DoPayments from '../views/payments/DoPayments'
import Reports from '../views/Reports'
import StoreInvoicePayments from '../views/StoreInvoicePayments/List'
import CreateStore from '../views/stores/Create'
import EditStore from '../views/stores/Edit'
import ListStores from '../views/stores/List'
import StoreUsers from '../views/stores/Users'
import EditUser from '../views/users/Edit'
import ShowUser from '../views/users/Show'

import { PAGE_NAME_MAPPING } from '@/lib'
import NoAccess from '@/views/NoAccess.vue'

const redirectToIdentityLogin = () => {
  const redirectUrl = new URL('/auth/login', config.identityAppUrl)
  redirectUrl.searchParams.append('app', 'admin')
  redirectUrl.searchParams.append('returnUrl', window.location.origin)
  window.location.replace(redirectUrl.toString())
}

const authenticate = async (to, from, next) => {
  const isAuthenticated = await Store.dispatch('auth/authenticate')

  if (!isAuthenticated) {
    next(false)
    redirectToIdentityLogin()
    return
  }

  await Store.dispatch('user/fetchUser')

  if (to.path === '/no-access') {
    return next({ path: '/#/no-access' })
  }

  if (!Store.getters['user/isAdmin']) {
    return next({ path: '/no-access' })
  }

  if (!Store.getters['user/isLoggedIn']) {
    next(false)
    redirectToIdentityLogin()
    return
  }

  return next()
}

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/login',
      redirect: (to) => {
        const returnUrl = new URL(to.fullPath, window.location.origin)
        const redirectUrl = new URL('/auth/login', config.identityAppUrl)
        redirectUrl.searchParams.append('app', 'admin')
        redirectUrl.searchParams.append('returnUrl', returnUrl)
        window.location.replace(redirectUrl)
        return '/'
      },
    },
    {
      path: '/logout',
      component: LogoutView,
      meta: {
        title: PAGE_NAME_MAPPING.logout,
      },
    },
    {
      path: '/no-access',
      name: 'no-access',
      component: NoAccess,
      meta: { title: 'You have no access' },
    },
    {
      path: '/',
      beforeEnter: authenticate,
      component: MainLayout,
      children: [
        {
          path: '/',
          component: Dashboard,
          meta: {
            title: PAGE_NAME_MAPPING.dashboard,
          },
        },
        {
          path: '/stores',
          component: ListStores,
          meta: {
            title: PAGE_NAME_MAPPING.advertisers,
          },
        },
        {
          path: '/stores/:id/edit',
          component: EditStore,
          meta: {
            title: PAGE_NAME_MAPPING.editAdvertiser,
          },
        },
        {
          path: '/stores/:id/users',
          component: StoreUsers,
          meta: {
            title: PAGE_NAME_MAPPING.advertiserUsersList,
          },
        },
        {
          path: '/stores/create',
          component: CreateStore,
          meta: {
            title: PAGE_NAME_MAPPING.createAdvertiser,
          },
        },
        {
          path: '/clients',
          component: ListClients,
          meta: {
            title: PAGE_NAME_MAPPING.clients,
          },
        },
        {
          path: '/clients/create',
          component: CreateClient,
          beforeEnter: authenticate,
          meta: {
            title: PAGE_NAME_MAPPING.createClient,
          },
        },
        {
          path: '/clients/:id/edit',
          component: EditClient,
          meta: {
            title: PAGE_NAME_MAPPING.editClient,
          },
        },
        {
          path: '/clients/:id/users',
          component: ClientUsers,
          meta: {
            title: PAGE_NAME_MAPPING.clientUsersList,
          },
        },
        {
          path: '/users/:id',
          component: ShowUser,
          beforeEnter: authenticate,
          meta: {
            title: PAGE_NAME_MAPPING.creatorView,
          },
        },
        {
          path: '/users/:id/edit',
          component: EditUser,
          meta: {
            title: PAGE_NAME_MAPPING.editCreator,
          },
        },
        {
          path: '/creators/:id',
          component: ShowUser,
          meta: {
            title: PAGE_NAME_MAPPING.creatorView,
          },
        },
        {
          path: '/creators/:id/edit',
          component: EditUser,
          meta: {
            title: PAGE_NAME_MAPPING.editCreator,
          },
        },
        {
          path: '/links/:id',
          component: UserLinks,
          meta: {
            title: PAGE_NAME_MAPPING.links,
          },
        },
        {
          path: '/redirect-posts',
          redirect: '/return-ads',
        },
        {
          path: '/redirect-posts/create',
          redirect: '/return-ads/create',
        },
        {
          path: '/redirect-posts/:id/edit',
          redirect: (to) => `/return-ads/${to.params.id}/edit`,
        },
        {
          path: '/payments',
          component: CreatePayments,
          meta: {
            title: PAGE_NAME_MAPPING.payments,
          },
        },
        {
          path: '/payments/do',
          component: DoPayments,
          meta: {
            title: PAGE_NAME_MAPPING.doPayment,
          },
        },
        {
          path: '/storeInvoicePayments',
          component: StoreInvoicePayments,
          meta: {
            title: PAGE_NAME_MAPPING.advertiserInvoicePayments,
          },
        },
        {
          path: '/clientInvoicePayments',
          name: 'Client invoice payments',
          component: ClientInvoicePayments,
          meta: {
            title: PAGE_NAME_MAPPING.clientInvoicePayments,
          },
        },
        {
          path: '/reports',
          component: Reports,
          meta: {
            title: PAGE_NAME_MAPPING.reports,
          },
        },
      ],
    },
  ],
})

router.beforeEach((to, from, next) => {
  document.title = to.meta?.title ? `${to.meta.title} | Metapic Admin` : 'Metapic Admin'
  next()
})

export default router
